import { useSelector } from "react-redux";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { selectOrders } from "../../../../../../store/selectors/OrderSlice";
import { Order, OrderState } from "../../../../../../entity/Order";
import "./ConfirmationOrder.css";
import { useTranslation } from "react-i18next";

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

export const ConfirmationOrder = (): React.ReactElement => {
  const { t } = useTranslation();
  const orders = useSelector(selectOrders);

  // Calculate confirmation statistics
  const confirmedOrders = orders.filter(
    (order: Order) => order.status === OrderState.CONFIRMED,
  );
  const canceledOrders = orders.filter(
    (order: Order) => order.status === OrderState.CANCELED,
  );
  const pendingOrders = orders.filter(
    (order: Order) => order.status === OrderState.PENDING,
  );
  const otherOrders = orders.filter(
    (order: Order) =>
      order.status !== OrderState.CONFIRMED &&
      order.status !== OrderState.CANCELED &&
      order.status !== OrderState.PENDING,
  );

  const totalOrders = orders.length;
  const confirmedCount = confirmedOrders.length;
  const canceledCount = canceledOrders.length;
  const pendingCount = pendingOrders.length;
  const otherCount = otherOrders.length;

  const confirmedPercent =
    totalOrders > 0 ? ((confirmedCount / totalOrders) * 100).toFixed(1) : "0";
  const canceledPercent =
    totalOrders > 0 ? ((canceledCount / totalOrders) * 100).toFixed(1) : "0";
  const pendingPercent =
    totalOrders > 0 ? ((pendingCount / totalOrders) * 100).toFixed(1) : "0";

  // Calculate confirmation rate (confirmed vs total non-pending)
  const processedOrders = totalOrders - pendingCount;
  const confirmationRate =
    processedOrders > 0
      ? ((confirmedCount / processedOrders) * 100).toFixed(1)
      : "0";

  // Chart data
  const chartData = {
    labels: ["Confirmed", "Canceled", "Pending", "Other"],
    datasets: [
      {
        data: [confirmedCount, canceledCount, pendingCount, otherCount],
        backgroundColor: [
          "#10b981", // Green for confirmed
          "#ef4444", // Red for canceled
          "#f59e0b", // Orange for pending
          "#6b7280", // Gray for other
        ],
        borderColor: "#ffffff",
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 13,
          weight: "600" as const,
        },
        bodyFont: {
          size: 12,
        },
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const percentage =
              totalOrders > 0 ? ((value / totalOrders) * 100).toFixed(1) : "0";
            return `${label}: ${value} ${t("stats.orders")} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "70%",
  };

  return (
    <div className="confirmation-order-container">
      {/* Header */}
      <div className="confirmation-header">
        <div className="header-content">
          <h2 className="confirmation-title">
            {t("stats.order_confirmation_analytics")}
          </h2>
          <p className="confirmation-subtitle">
            {t("stats.track_confirmation_and_cancellation_rates")}
          </p>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="confirmation-grid">
        {/* Left: Chart Section */}
        <div className="chart-section">
          <div className="chart-card">
            <h3 className="chart-card-title">
              {t("stats.order_distribution")}
            </h3>
            <div className="doughnut-wrapper">
              <div className="doughnut-container">
                <Doughnut data={chartData} options={chartOptions as any} />
                <div className="doughnut-center">
                  <div className="center-value">{totalOrders}</div>
                  <div className="center-label">{t("stats.total_orders")}</div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-dot confirmed"></div>
                <span className="legend-label">{t("stats.confirmed")}</span>
                <span className="legend-value">{confirmedCount}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot canceled"></div>
                <span className="legend-label">{t("stats.canceled")}</span>
                <span className="legend-value">{canceledCount}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot pending"></div>
                <span className="legend-label">{t("stats.pending")}</span>
                <span className="legend-value">{pendingCount}</span>
              </div>
              <div className="legend-item">
                <div className="legend-dot other"></div>
                <span className="legend-label">{t("stats.other")}</span>
                <span className="legend-value">{otherCount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Stats Cards */}
        <div className="stats-section">
          {/* Confirmation Rate Card */}
          <div className="metric-card highlight">
            <div className="metric-icon-wrapper success">
              <svg
                className="metric-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="metric-content">
              <p className="metric-label">{t("stats.confirmation_rate")}</p>
              <p className="metric-value large">
                {confirmationRate}
                <span className="metric-unit">%</span>
              </p>
              <p className="metric-description">
                {t("stats.of")} {processedOrders} {t("stats.processed_orders")}
              </p>
            </div>
          </div>

          {/* Confirmed Orders Card */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-sm confirmed">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="metric-badge success">{confirmedPercent}%</span>
            </div>
            <p className="metric-label">{t("stats.confirmed_orders")}</p>
            <p className="metric-value">{confirmedCount}</p>
            <div className="metric-bar">
              <div
                className="metric-bar-fill confirmed"
                style={{ width: `${confirmedPercent}%` }}
              ></div>
            </div>
          </div>

          {/* Canceled Orders Card */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-sm canceled">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </div>
              <span className="metric-badge danger">{canceledPercent}%</span>
            </div>
            <p className="metric-label">{t("stats.canceled_orders")}</p>
            <p className="metric-value">{canceledCount}</p>
            <div className="metric-bar">
              <div
                className="metric-bar-fill canceled"
                style={{ width: `${canceledPercent}%` }}
              ></div>
            </div>
          </div>

          {/* Pending Orders Card */}
          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon-sm pending">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <span className="metric-badge warning">{pendingPercent}%</span>
            </div>
            <p className="metric-label">{t("stats.pending_orders")}</p>
            <p className="metric-value">{pendingCount}</p>
            <div className="metric-bar">
              <div
                className="metric-bar-fill pending"
                style={{ width: `${pendingPercent}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Insight Card */}
      <div className="insight-card">
        <div className="insight-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div className="insight-content">
          <h4 className="insight-title">{t("stats.performance_insight")}</h4>
          <p className="insight-text">
            {parseFloat(confirmationRate) >= 70
              ? t("stats.great_job", { rate: confirmationRate })
              : parseFloat(confirmationRate) >= 50
                ? t("stats.moderate_rate", { rate: confirmationRate })
                : `Your confirmation rate of ${confirmationRate}% needs attention. Focus on reducing cancellations through better customer engagement.`}
          </p>
        </div>
      </div>
    </div>
  );
};
